import SiteButton from "components/Site/SiteButton";
import React, { useEffect, useRef, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";
import { toast, Slide } from "react-toastify";

import saveContact from "api/ContactUsForm";

const RECAPTCHA_SITE_KEY = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || "").trim();

// Anti-spam thresholds. A real visitor needs a few seconds to fill the form in,
// and has no reason to send twice in a row.
const MIN_FILL_TIME_MS = 3000;
const RESUBMIT_COOLDOWN_MS = 30000;

const toastOptions = {
  position: "top-center",
  autoClose: 2000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: false,
  draggable: true,
  progress: undefined,
  theme: "dark",
  transition: Slide,
};

const SUCCESS_MESSAGE = "Thank you for contacting us! We'll get back to you soon.";

export const ContactUsForm = () => {
  const formRef = useRef(null);
  const recaptchaRef = useRef(null);
  const mountedAtRef = useRef(Date.now());
  const lastSubmitAtRef = useRef(0);

  const [message, setMessage] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [formBeingSubmitted, setFormBeingSubmitted] = useState(false);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      console.error(
        "REACT_APP_RECAPTCHA_SITE_KEY is not set. Add it to .env and restart the dev server " +
          "(Create React App inlines REACT_APP_* variables at build time)."
      );
    }
  }, []);

  const notify = (text, type) => {
    if (type === "success") {
      toast.success(text, toastOptions);
    } else if (type === "error") {
      toast.error(text, toastOptions);
    }
  };

  const resetRecaptcha = () => {
    recaptchaRef.current?.reset();
    setRecaptchaToken(null);
  };

  const resetForm = () => {
    formRef.current?.reset();
    setMessage("");
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const data = new FormData(formRef.current);

    // Honeypot: no human ever fills this in. Show the usual success so bots
    // cannot tell they were filtered, and send nothing.
    if (data.get("company_website")) {
      notify(SUCCESS_MESSAGE, "success");
      resetForm();
      resetRecaptcha();
      return;
    }

    // Submitted faster than a person could type: same silent no-op.
    if (Date.now() - mountedAtRef.current < MIN_FILL_TIME_MS) {
      notify(SUCCESS_MESSAGE, "success");
      resetForm();
      resetRecaptcha();
      return;
    }

    if (Date.now() - lastSubmitAtRef.current < RESUBMIT_COOLDOWN_MS) {
      notify("Please wait a moment before sending another message.", "error");
      return;
    }

    if (!recaptchaToken) {
      notify("Please confirm you are not a robot.", "error");
      return;
    }

    setFormBeingSubmitted(true);
    const response = await saveContact(data);
    setFormBeingSubmitted(false);

    // reCAPTCHA v2 tokens are single-use, so clear the widget after every attempt.
    resetRecaptcha();

    if (response) {
      lastSubmitAtRef.current = Date.now();
      notify(SUCCESS_MESSAGE, "success");
      resetForm();
    } else {
      notify("Some Error Occurred. Please try again later.", "error");
    }
  };

  if (!RECAPTCHA_SITE_KEY) {
    return (
      <Alert variant="warning" className="rounded-0">
        This form is temporarily unavailable. Please email us directly.
        {process.env.NODE_ENV !== "production" && (
          <div className="mt-2 small">
            Developer note: REACT_APP_RECAPTCHA_SITE_KEY is missing. Set it in .env and restart the
            dev server.
          </div>
        )}
      </Alert>
    );
  }

  return (
    <Form ref={formRef} onSubmit={submitHandler}>
      <Form.Group className="mb-3">
        <Form.Label className="mb-1 mb-sm-2">Your Name</Form.Label>
        <Form.Control
          type="text"
          className="py-2 rounded-0"
          id="name"
          placeholder=""
          name="name"
          required
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label className="mb-1 mb-sm-2">Mobile Number</Form.Label>
        <Form.Control
          type="tel"
          className="py-2 rounded-0"
          id="phone"
          name="phone"
          placeholder=""
          required
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label className="mb-1 mb-sm-2">Email</Form.Label>
        <Form.Control
          type="email"
          className="py-2 rounded-0"
          id="email"
          name="email"
          placeholder=""
          required
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label className="mb-1 mb-sm-2">Message</Form.Label>
        <Form.Control
          as="textarea"
          className="py-2 rounded-0"
          id="message"
          name="message"
          rows="5"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        ></Form.Control>
      </Form.Group>

      {/* Honeypot - kept off-screen rather than display:none so bots still fill it. */}
      <input
        type="text"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />

      <div className="d-flex justify-content-center">
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={RECAPTCHA_SITE_KEY}
          onChange={(token) => setRecaptchaToken(token)}
          onExpired={() => setRecaptchaToken(null)}
          onErrored={() => setRecaptchaToken(null)}
        />
      </div>

      <div className="d-flex justify-content-center mt-4">
        <SiteButton
          title="Send"
          type="submit"
          style={{ minWidth: 150 }}
          loading={formBeingSubmitted}
          disabled={!recaptchaToken}
        />
      </div>
    </Form>
  );
};

export default ContactUsForm;
